/**
 * src/types/chart.registry.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * THE REGISTRY — single source of truth for all chart type metadata.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  ADDING A NEW CHART TYPE — complete checklist                           │
 * │                                                                         │
 * │  1. Create  src/types/charts/<name>.chart.ts                            │
 * │     Define: AnimationStyle type + array, DataPoint, SpacingConfig,      │
 * │             DEFAULTS object.                                             │
 * │                                                                         │
 * │  2. Add to STEP A — import the four exports from your new file.         │
 * │  3. Add to STEP B — add '<name>' to ChartType.                          │
 * │  4. Add to STEP C — add your types to AnyDataPoint and SpacingConfig.   │
 * │  5. Add to STEP D — add '<name>: YOUR_DEFAULTS' to CHART_DEFAULTS.      │
 * │  6. Add to STEP E — add '<name>: YOUR_STYLES_ARRAY' to VALID_ANIMATION  │
 * │                      and ELEMENT_PANEL_ANIM_STYLES.                     │
 * │                                                                         │
 * │  Then follow the existing ADDING A NEW CHART TYPE implementation        │
 * │  checklist in chart.core.types.ts (renderer, ANIMATION_STYLE_REGISTRY,  │
 * │  schema notes, etc.).                                                   │
 * │                                                                         │
 * │  No other type files need to change.                                    │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Chart2Video | CORSphere Software Inc.
 * Schema version: 3
 */

// ─────────────────────────────────────────────────────────────────────────────
// STEP A — CHART MODULE IMPORTS
// One import block per chart type. Add a new block here when adding a chart.
// ─────────────────────────────────────────────────────────────────────────────

import type { ChartConfigBase } from './core/chart-config.base';

import type { BarDataPoint, BarSpacingConfig }                         from './charts/bar.chart';
import { BAR_DEFAULTS, BAR_ANIMATION_STYLES, BAR_ELEMENT_PANEL_STYLES } from './charts/bar.chart';

import type { LineDataPoint, LineSpacingConfig, LineSeriesStyle }              from './charts/line.chart';
import { LINE_DEFAULTS, LINE_ANIMATION_STYLES, LINE_ELEMENT_PANEL_STYLES }   from './charts/line.chart';

import type { PieDataPoint, PieSpacingConfig }                             from './charts/pie.chart';
import { PIE_DEFAULTS, PIE_ANIMATION_STYLES, PIE_ELEMENT_PANEL_STYLES }   from './charts/pie.chart';

import type { HeatMapDataPoint, HeatMapSpacingConfig }                               from './charts/heatmap.chart';
import { HEATMAP_DEFAULTS, HEATMAP_ANIMATION_STYLES, HEATMAP_ELEMENT_PANEL_STYLES } from './charts/heatmap.chart';

import type { BarHorizontalDataPoint, BarHorizontalSpacingConfig }                                        from './charts/bar-horizontal.chart';
import { BAR_HORIZONTAL_DEFAULTS, BAR_HORIZONTAL_ANIMATION_STYLES, BAR_HORIZONTAL_ELEMENT_PANEL_STYLES } from './charts/bar-horizontal.chart';

import type { BarStackedDataPoint, BarStackedSpacingConfig }                                     from './charts/bar-stacked.chart';
import { BAR_STACKED_DEFAULTS, BAR_STACKED_ANIMATION_STYLES, BAR_STACKED_ELEMENT_PANEL_STYLES } from './charts/bar-stacked.chart';

import type { BarRaceDataPoint, BarRaceSpacingConfig }                                  from './charts/bar-race.chart';
import { BAR_RACE_DEFAULTS, BAR_RACE_ANIMATION_STYLES, BAR_RACE_ELEMENT_PANEL_STYLES } from './charts/bar-race.chart';

import type { ScatterDataPoint, ScatterSpacingConfig }                                     from './charts/scatter.chart';
import { SCATTER_DEFAULTS, SCATTER_ANIMATION_STYLES, SCATTER_ELEMENT_PANEL_STYLES }       from './charts/scatter.chart';

import type { GaugeDataPoint, GaugeSpacingConfig }                                  from './charts/gauge.chart';
import { GAUGE_DEFAULTS, GAUGE_ANIMATION_STYLES, GAUGE_ELEMENT_PANEL_STYLES }       from './charts/gauge.chart';

import type { RadarDataPoint, RadarSpacingConfig }                                  from './charts/radar.chart';
import { RADAR_DEFAULTS, RADAR_ANIMATION_STYLES, RADAR_ELEMENT_PANEL_STYLES }       from './charts/radar.chart';

import type { WaterfallDataPoint, WaterfallSpacingConfig }                                     from './charts/waterfall.chart';
import { WATERFALL_DEFAULTS, WATERFALL_ANIMATION_STYLES, WATERFALL_ELEMENT_PANEL_STYLES }      from './charts/waterfall.chart';

import type { FunnelDataPoint, FunnelSpacingConfig }                                  from './charts/funnel.chart';
import { FUNNEL_DEFAULTS, FUNNEL_ANIMATION_STYLES, FUNNEL_ELEMENT_PANEL_STYLES }     from './charts/funnel.chart';

import type { BarGroupedDataPoint, BarGroupedSpacingConfig }                                     from './charts/bar-grouped.chart';
import { BAR_GROUPED_DEFAULTS, BAR_GROUPED_ANIMATION_STYLES, BAR_GROUPED_ELEMENT_PANEL_STYLES } from './charts/bar-grouped.chart';

// Re-export all chart-level types so other files can import directly from here
export type { BarDataPoint, BarSpacingConfig } from './charts/bar.chart';
export type { LineDataPoint, LineSpacingConfig, LineSeriesStyle } from './charts/line.chart';
export type { PieDataPoint, PieSpacingConfig } from './charts/pie.chart';
export type { HeatMapDataPoint, HeatMapSpacingConfig, HeatMapCellValue } from './charts/heatmap.chart';
export type { GaugeZone } from './charts/gauge.chart';
export type { BarHorizontalDataPoint, BarHorizontalSpacingConfig } from './charts/bar-horizontal.chart';
export type { BarStackedDataPoint, BarStackedSpacingConfig } from './charts/bar-stacked.chart';
export type { BarRaceDataPoint, BarRaceSpacingConfig } from './charts/bar-race.chart';
export type { ScatterDataPoint, ScatterSpacingConfig } from './charts/scatter.chart';
export type { GaugeDataPoint, GaugeSpacingConfig } from './charts/gauge.chart';
export type { RadarDataPoint, RadarSpacingConfig } from './charts/radar.chart';
export type { WaterfallDataPoint, WaterfallSpacingConfig } from './charts/waterfall.chart';
export type { FunnelDataPoint, FunnelSpacingConfig } from './charts/funnel.chart';
export type { BarGroupedDataPoint, BarGroupedSpacingConfig } from './charts/bar-grouped.chart';

// Re-export animation styles
export type { BarAnimationStyle } from './charts/bar.chart';
export type { BarHorizontalAnimationStyle } from './charts/bar-horizontal.chart';
export type { BarStackedAnimationStyle } from './charts/bar-stacked.chart';
export type { BarRaceAnimationStyle } from './charts/bar-race.chart';
export type { LineAnimationStyle } from './charts/line.chart';
export type { PieAnimationStyle } from './charts/pie.chart';
export type { HeatMapAnimationStyle } from './charts/heatmap.chart';
export type { ScatterAnimationStyle } from './charts/scatter.chart';
export type { GaugeAnimationStyle } from './charts/gauge.chart';
export type { RadarAnimationStyle } from './charts/radar.chart';
export type { WaterfallAnimationStyle } from './charts/waterfall.chart';
export type { FunnelAnimationStyle } from './charts/funnel.chart';
export type { BarGroupedAnimationStyle } from './charts/bar-grouped.chart';


// ─────────────────────────────────────────────────────────────────────────────
// STEP B — CHART TYPE LITERAL
// Add '<name>' here when adding a new chart type.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All supported chart type identifiers.
 * This is the discriminant for every data, spacing, and defaults lookup.
 *
 * Availability tiers:
 *   always-on    — bar, line, pie, heatmap
 *   Sprint C     — bar-horizontal, bar-stacked, bar-race, scatter, gauge
 *   future       — radar, waterfall, funnel
 */
export type ChartType =
  // ── Always available ──────────────────────────────────────────────────────
  | 'bar'
  | 'line'
  | 'pie'
  | 'heatmap'
  // ── Sprint C ──────────────────────────────────────────────────────────────
  | 'bar-horizontal'
  | 'bar-stacked'
  | 'bar-grouped'
  | 'bar-race'
  | 'scatter'
  | 'gauge'
  // ── Future ────────────────────────────────────────────────────────────────
  | 'radar'
  | 'waterfall'
  | 'funnel';

/** Flat list for runtime validation loops. Keep in sync with ChartType. */
export const VALID_CHART_TYPES: ChartType[] = [
  'bar', 'line', 'pie', 'heatmap',
  'bar-horizontal', 'bar-stacked', 'bar-grouped', 'bar-race',
  'scatter', 'gauge',
  'radar', 'waterfall', 'funnel',
];


// ─────────────────────────────────────────────────────────────────────────────
// STEP C — DISCRIMINATED UNION TYPES
// Add your new DataPoint and SpacingConfig types here.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Union of all concrete data point types.
 * Narrow via the `chartType` discriminant:
 *   if (point.chartType === 'scatter') { point.x, point.y ... }
 */
export type AnyDataPoint =
  | BarDataPoint
  | BarHorizontalDataPoint
  | BarStackedDataPoint
  | BarGroupedDataPoint
  | BarRaceDataPoint
  | LineDataPoint
  | PieDataPoint
  | HeatMapDataPoint
  | ScatterDataPoint
  | GaugeDataPoint
  | RadarDataPoint
  | WaterfallDataPoint
  | FunnelDataPoint;

/**
 * Discriminated union of all chart-type spacing configs.
 * The `chartType` field on each variant is the discriminant.
 * spacing.chartType must always match the top-level ChartConfig.chartType.
 */
export type SpacingConfig =
  | BarSpacingConfig
  | BarHorizontalSpacingConfig
  | BarStackedSpacingConfig
  | BarGroupedSpacingConfig
  | BarRaceSpacingConfig
  | LineSpacingConfig
  | PieSpacingConfig
  | HeatMapSpacingConfig
  | ScatterSpacingConfig
  | GaugeSpacingConfig
  | RadarSpacingConfig
  | WaterfallSpacingConfig
  | FunnelSpacingConfig;

/**
 * Union of all per-chart animation style string types.
 * Broad on purpose — validation against the specific chart type happens at
 * runtime via VALID_ANIMATION_STYLES[chartType].
 */
export type AnimationStyle =
  | 'classic' | 'sequential' | 'staggered' | 'elastic' | 'wave' | 'reveal' | 'fadeScale' | 'popIn'
  | 'drawLine' | 'trace' | 'growFromAxis' | 'segmentBySegment' | 'pulse' | 'cascade'
  | 'growFromCenter' | 'spinIn' | 'unfold' | 'separation' | 'wipe'
  | 'rowByRow' | 'columnByColumn' | 'zoom' | 'fadeIn'
  | 'growUp'
  | 'sweep' | 'bounce';


// ─────────────────────────────────────────────────────────────────────────────
// CHART CONFIG INTERFACE
// Defined here because ChartConfig uses SpacingConfig and ChartType, both of
// which live in this file.
// chart modules use ChartConfigBase<S> (from core/) to avoid circular deps.
// ─────────────────────────────────────────────────────────────────────────────

export type { ChartConfigBase } from './core/chart-config.base';

/**
 * Complete configuration for one chart.
 * This is what hooks serialise, agents read/write, and Firestore stores.
 *
 * ChartConfig = ChartConfigBase narrowed to the full SpacingConfig discriminated
 * union. `spacing.chartType` must always match the top-level `chartType`.
 * `axes` is absent for charts with no traditional x/y axes (pie, gauge, radar, funnel).
 */
export type ChartConfig = ChartConfigBase<SpacingConfig> & { chartType: ChartType };


// ─────────────────────────────────────────────────────────────────────────────
// STEP D — CHART DEFAULTS
// Add '<name>: YOUR_DEFAULTS' when adding a new chart type.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default ChartConfig for every supported chart type.
 * TypeScript enforces completeness — every ChartType literal must have an entry.
 */
export const CHART_DEFAULTS: Record<ChartType, ChartConfigBase<SpacingConfig>> = {
  bar:              BAR_DEFAULTS,
  'bar-horizontal': BAR_HORIZONTAL_DEFAULTS,
  'bar-stacked':    BAR_STACKED_DEFAULTS,
  'bar-grouped':    BAR_GROUPED_DEFAULTS,
  'bar-race':       BAR_RACE_DEFAULTS,
  line:             LINE_DEFAULTS,
  pie:              PIE_DEFAULTS,
  heatmap:          HEATMAP_DEFAULTS,
  scatter:          SCATTER_DEFAULTS,
  gauge:            GAUGE_DEFAULTS,
  radar:            RADAR_DEFAULTS,
  waterfall:        WATERFALL_DEFAULTS,
  funnel:           FUNNEL_DEFAULTS,
};


// ─────────────────────────────────────────────────────────────────────────────
// STEP E — ANIMATION STYLE MAPS
// Add your chart's animation style array in both maps when adding a chart type.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valid animation styles per chart type.
 * Used by quickValidateChartConfig and the UI dropdowns.
 */
export const VALID_ANIMATION_STYLES: Record<ChartType, readonly string[]> = {
  bar:              BAR_ANIMATION_STYLES,
  'bar-horizontal': BAR_HORIZONTAL_ANIMATION_STYLES,
  'bar-stacked':    BAR_STACKED_ANIMATION_STYLES,
  'bar-grouped':    BAR_GROUPED_ANIMATION_STYLES,
  'bar-race':       BAR_RACE_ANIMATION_STYLES,
  line:             LINE_ANIMATION_STYLES,
  pie:              PIE_ANIMATION_STYLES,
  heatmap:          HEATMAP_ANIMATION_STYLES,
  scatter:          SCATTER_ANIMATION_STYLES,
  gauge:            GAUGE_ANIMATION_STYLES,
  radar:            RADAR_ANIMATION_STYLES,
  waterfall:        WATERFALL_ANIMATION_STYLES,
  funnel:           FUNNEL_ANIMATION_STYLES,
};

/**
 * Animation style options available in ElementPanel per chart type.
 * Excludes whole-chart-only styles (e.g. 'drawLine') that are not valid
 * as per-point overrides.
 * Chart types with empty arrays don't support per-element animation overrides.
 */
export const ELEMENT_PANEL_ANIM_STYLES: Record<ChartType, readonly string[]> = {
  bar:              BAR_ELEMENT_PANEL_STYLES,
  'bar-horizontal': BAR_HORIZONTAL_ELEMENT_PANEL_STYLES,
  'bar-stacked':    BAR_STACKED_ELEMENT_PANEL_STYLES,
  'bar-grouped':    BAR_GROUPED_ELEMENT_PANEL_STYLES,
  'bar-race':       BAR_RACE_ELEMENT_PANEL_STYLES,
  line:             LINE_ELEMENT_PANEL_STYLES,
  pie:              PIE_ELEMENT_PANEL_STYLES,
  heatmap:          HEATMAP_ELEMENT_PANEL_STYLES,
  scatter:          SCATTER_ELEMENT_PANEL_STYLES,
  gauge:            GAUGE_ELEMENT_PANEL_STYLES,
  radar:            RADAR_ELEMENT_PANEL_STYLES,
  waterfall:        WATERFALL_ELEMENT_PANEL_STYLES,
  funnel:           FUNNEL_ELEMENT_PANEL_STYLES,
};
